import {uniqBy} from 'lodash';
import {
  ApiParams,
  ApiStatus,
  ICurrentUserPermissions,
  IDatabase,
  IDatabasesMetadata,
  ResourceSharingSettings,
  SharingSettingsTypes,
} from '../../models';
import {DatabasesAction, DatabasesActionTypes} from '../actions/databaseActions';
import {removeElementFromList, sortElementsInList} from '../../utils/reducers';
import {ILoadedSharingSettingsAction, SharingSettingsActionsTypes} from '../actions/sharingSettingsActions';

export const initialDatabaseState: IDatabaseState = {
  loadingListStatus: ApiStatus.LOADING,
  loadingStatus: ApiStatus.LOADING,
  loadingMetadataStatus: ApiStatus.LOADING,
  loadingMostRecentStatus: ApiStatus.LOADING,
  creatingStatus: null,
  removingStatus: ApiStatus.LOADING,
  updatingStatus: null,
  databases: [],
  canLoadMoreDatabases: false,
  canLoadMoreSchemasForDatabase: false,
  databasesParams: {},
  databasesMetadata: null,
  database: null,
  lastCreatedDatabase: null,
  currentSharingSettings: null,
  currentUserPermissions: null,
  mostRecentDatabase: null,
}

export interface IDatabaseState {
  loadingListStatus: ApiStatus;
  loadingStatus: ApiStatus;
  loadingMetadataStatus: ApiStatus;
  loadingMostRecentStatus: ApiStatus;
  creatingStatus: ApiStatus | null;
  updatingStatus: ApiStatus | null;
  removingStatus: ApiStatus;
  databases: IDatabase[];
  canLoadMoreDatabases: boolean;
  canLoadMoreSchemasForDatabase: boolean;
  databasesParams: ApiParams | undefined;
  databasesMetadata: IDatabasesMetadata | null;
  database: IDatabase | null;
  lastCreatedDatabase: IDatabase | null;
  currentSharingSettings: ResourceSharingSettings | null;
  currentUserPermissions: ICurrentUserPermissions | null;
  mostRecentDatabase: IDatabase | null;
}

export default function databasesReducer(
  state: IDatabaseState = initialDatabaseState,
  action: DatabasesAction | ILoadedSharingSettingsAction
): IDatabaseState {
  switch (action.type) {
    case DatabasesActionTypes.CAN_LOAD_MORE_DATABASES:
      return {
        ...state,
        canLoadMoreDatabases: action.payload.canLoadMore
      };
    case DatabasesActionTypes.CAN_LOAD_MORE_SCHEMAS_FOR_DATABASE:
      return {
        ...state,
        canLoadMoreSchemasForDatabase: action.payload.canLoadMoreSchemas
      };
    // List
    case DatabasesActionTypes.LOAD_DATABASES:
    case DatabasesActionTypes.LOADING_DATABASES:
      return {
        ...state,
        loadingListStatus: ApiStatus.LOADING
      };

    case DatabasesActionTypes.LOADING_DATABASES_FAILED:
      return {
        ...state,
        loadingListStatus: ApiStatus.FAILED
      };

    case DatabasesActionTypes.LOADED_DATABASES:
      return {
        ...state,
        loadingListStatus: ApiStatus.LOADED,
        databases: action.payload.reload ? action.payload.databases : uniqBy([...state.databases, ...action.payload.databases],'uid'),
        databasesParams: action.payload.params
      };

    case DatabasesActionTypes.LOAD_DATABASES_METADATA:
    case DatabasesActionTypes.LOADING_DATABASES_METADATA:
      return {
        ...state,
        loadingMetadataStatus: ApiStatus.LOADING
      };

    case DatabasesActionTypes.LOADING_DATABASES_METADATA_FAILED:
      return {
        ...state,
        loadingMetadataStatus: ApiStatus.FAILED
      };

    case DatabasesActionTypes.LOADED_DATABASES_METADATA:
      return {
        ...state,
        loadingMetadataStatus: ApiStatus.LOADED,
        databasesMetadata: action.payload.databasesMetadata
      };

    // Details
    case DatabasesActionTypes.LOAD_DATABASE:
    case DatabasesActionTypes.LOADING_DATABASE:
      return {
        ...state,
        loadingStatus: ApiStatus.LOADING
      };

    case DatabasesActionTypes.LOADING_DATABASE_FAILED:
      return {
        ...state,
        loadingStatus: ApiStatus.FAILED
      };

    case DatabasesActionTypes.LOADED_DATABASE:
      return {
        ...state,
        loadingStatus: ApiStatus.LOADED,
        database: action.payload.database.schemas ? {
            ...action.payload.database,
            schemas: [...action.payload.reload ? action.payload.database.schemas :
              [...state?.database?.schemas as any, ...action.payload.database.schemas]]
          } :
          {...action.payload.database}
      };

    case DatabasesActionTypes.UNSET_DATABASE:
      return {
        ...state,
        database: null,
      }

    case DatabasesActionTypes.LOAD_MOST_RECENT_DATABASE:
    case DatabasesActionTypes.LOADING_MOST_RECENT_DATABASE:
      return {
        ...state,
        loadingMostRecentStatus: ApiStatus.LOADING
      }

    case DatabasesActionTypes.LOADING_MOST_RECENT_DATABASE_FAILED:
      return {
        ...state,
        loadingMostRecentStatus: ApiStatus.FAILED
      }

    case DatabasesActionTypes.LOADED_MOST_RECENT_DATABASE:
      return {
        ...state,
        loadingMostRecentStatus: ApiStatus.LOADED,
        mostRecentDatabase: action.payload.most_recent_database,
      }

    case DatabasesActionTypes.LOADED_DATABASE_SHARING_SETTINGS:
      return {
        ...state,
        databases: state.databases.map(database => {
          if (database.uid === action.payload.uid) {
            return {
              ...database,
              sharing_settings: action.payload.sharingSettings
            }
          }
          return database;
        })
      };
    case SharingSettingsActionsTypes.LOADED_SHARING_SETTINGS:
      if (action.payload.type !== SharingSettingsTypes.DATABASE) {
        return state;
      }
      return {
        ...state,
        databases: state.databases.map(database => {
          if (database.uid === action.payload.uid) {
            return {
              ...database,
              sharing_settings: action.payload.settings
            }
          }
          return database;
        })
      };

    case DatabasesActionTypes.LOADED_CURRENT_USER_PERMISSIONS:
      return {
        ...state,
        currentUserPermissions: action.payload
      }

    case DatabasesActionTypes.SORT_SCHEMAS_IN_DATABASE:
      return {
        ...state,
        database: state.database ? {
          ...state.database,
          schemas: state.database.schemas ?
            sortElementsInList(state.database.schemas, action.payload.sort) :
            state.database.schemas
        } : state.database
      };

    // Create

    case DatabasesActionTypes.CREATE_DATABASE:
    case DatabasesActionTypes.CREATING_DATABASE:
      return {
        ...state,
        creatingStatus: ApiStatus.LOADING
      };

    case DatabasesActionTypes.CREATING_DATABASE_FAILED:
      return {
        ...state,
        creatingStatus: ApiStatus.FAILED
      };

    case DatabasesActionTypes.CREATED_DATABASE:
      return {
        ...state,
        creatingStatus: ApiStatus.LOADED,
        databases: state.databases ?
          state.databases.concat([action.payload]) :
          [action.payload],
        lastCreatedDatabase: action.payload
      };

    case DatabasesActionTypes.UNSET_LAST_CREATED_DATABASE:
      return {
        ...state,
        lastCreatedDatabase: action.payload,
        creatingStatus: null,
      }

    // Update

    case DatabasesActionTypes.EDIT_DATABASE:
    case DatabasesActionTypes.EDITING_DATABASE:
      return {
        ...state,
        updatingStatus: ApiStatus.LOADING
      };

    case DatabasesActionTypes.EDITING_DATABASE_FAILED:
      return {
        ...state,
        updatingStatus: ApiStatus.FAILED
      };

    case DatabasesActionTypes.EDITED_DATABASE:
      return {
        ...state,
        updatingStatus: ApiStatus.LOADED,
      };
    case DatabasesActionTypes.RESET_UPDATING_STATUS:
      return {
        ...state,
        updatingStatus: null
      };

    // Remove

    case DatabasesActionTypes.REMOVE_DATABASE:
    case DatabasesActionTypes.REMOVING_DATABASE:
      return {
        ...state,
        removingStatus: ApiStatus.LOADING
      };

    case DatabasesActionTypes.REMOVING_DATABASE_FAILED:
      return {
        ...state,
        removingStatus: ApiStatus.FAILED
      };

    case DatabasesActionTypes.REMOVED_DATABASE:
      return {
        ...state,
        removingStatus: ApiStatus.LOADED,
        databases: state.databases ?
          removeElementFromList(state.databases, {uid: action.payload}, 'uid') :
          state.databases
      };

    default:
      return state;
  }
}
